import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { filterNull, willNeverHappen } from "healthex-common/utils";
import { FirstFeaturedGap, FirstFeaturedGapDivider, FirstFeaturedGapDividerLine, FirstFeaturedGapText, IdpHintMessage, } from "./PatientConsentManagerShared.styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { getObservableT } from "../Infra/i18next";
const t = getObservableT("common");
export var AuthButtonOrderType;
(function (AuthButtonOrderType) {
    AuthButtonOrderType["DIRECT_LOGIN"] = "DIRECT_LOGIN";
    AuthButtonOrderType["LOGIN"] = "LOGIN";
    AuthButtonOrderType["LOGIN_CLEAR_ONLY"] = "LOGIN_CLEAR_ONLY";
    AuthButtonOrderType["REAUTH"] = "REAUTH";
    AuthButtonOrderType["TRY_AGAIN_CLEAR_LOGIN_ERROR"] = "TRY_AGAIN_CLEAR_LOGIN_ERROR";
    AuthButtonOrderType["TRY_AGAIN_SOCIAL_LOGIN_ERROR"] = "TRY_AGAIN_SOCIAL_LOGIN_ERROR";
    AuthButtonOrderType["TRY_AGAIN_CLEAR_ONLY"] = "TRY_AGAIN_CLEAR_ONLY";
    AuthButtonOrderType["REAUTH_LANDING"] = "REAUTH_LANDING";
})(AuthButtonOrderType || (AuthButtonOrderType = {}));
const AuthButtonsDefaultOrder = [
    GFIDProvider.GOOGLE,
    GFIDProvider.APPLE,
    GFIDProvider.MICROSOFT,
    GFIDProvider.CLEAR,
    "LAST",
];
const AuthButtonsDefaultOrderClearOnly = [GFIDProvider.CLEAR, "LAST"];
function getAuthButtonOrder(type, firstFeatureGFIDProvider, idpHint) {
    if (type === AuthButtonOrderType.DIRECT_LOGIN || type === AuthButtonOrderType.TRY_AGAIN_SOCIAL_LOGIN_ERROR) {
        const buttons = [...AuthButtonsDefaultOrder];
        if (firstFeatureGFIDProvider) {
            return featureProviderButtonToFirst(firstFeatureGFIDProvider, false, buttons);
        }
        else {
            return { buttons };
        }
    }
    else if (type === AuthButtonOrderType.LOGIN) {
        const normalizedIdpHint = idpHint === null || idpHint === void 0 ? void 0 : idpHint.toLowerCase();
        let hintProvider = null;
        let isHint = false;
        if (firstFeatureGFIDProvider) {
            hintProvider = firstFeatureGFIDProvider;
        }
        else {
            if (normalizedIdpHint) {
                if (normalizedIdpHint.includes("google")) {
                    hintProvider = GFIDProvider.GOOGLE;
                }
                else if (normalizedIdpHint.includes("apple")) {
                    hintProvider = GFIDProvider.APPLE;
                }
                else if (normalizedIdpHint.includes("microsoft")) {
                    hintProvider = GFIDProvider.MICROSOFT;
                }
                isHint = true;
            }
        }
        return featureProviderButtonToFirst(hintProvider, isHint);
    }
    else if (type === AuthButtonOrderType.TRY_AGAIN_CLEAR_LOGIN_ERROR) {
        return featureProviderButtonToFirst(GFIDProvider.CLEAR, false);
    }
    else if (type === AuthButtonOrderType.REAUTH) {
        return { buttons: [...AuthButtonsDefaultOrder] };
    }
    else if (type === AuthButtonOrderType.LOGIN_CLEAR_ONLY || type === AuthButtonOrderType.TRY_AGAIN_CLEAR_ONLY) {
        return { buttons: [...AuthButtonsDefaultOrderClearOnly] };
    }
    else if (type === AuthButtonOrderType.REAUTH_LANDING) {
        const provider = firstFeatureGFIDProvider !== null && firstFeatureGFIDProvider !== void 0 ? firstFeatureGFIDProvider : GFIDProvider.CLEAR;
        const featuredButton = provider === GFIDProvider.GOOGLE ||
            provider === GFIDProvider.APPLE ||
            provider === GFIDProvider.MICROSOFT ||
            provider === GFIDProvider.CLEAR
            ? provider
            : GFIDProvider.CLEAR;
        return { buttons: [featuredButton, "LAST"] };
    }
    else {
        willNeverHappen(type);
        return { buttons: [] };
    }
}
function featureProviderButtonToFirst(gfidProvider, isHint, defaultButtons) {
    let hasFirstFeatured = false;
    let featuredProvider = null;
    let buttons = defaultButtons !== null && defaultButtons !== void 0 ? defaultButtons : [...AuthButtonsDefaultOrder];
    if (gfidProvider) {
        const buttonIndex = buttons.findIndex((p) => p === gfidProvider);
        if (buttonIndex !== -1) {
            const [removedButton] = buttons.splice(buttonIndex, 1);
            buttons = [removedButton, ...buttons];
            hasFirstFeatured = true;
            featuredProvider = gfidProvider;
        }
    }
    return { hasFirstFeatured, featuredProvider, isHint, buttons };
}
export function renderAuthButtons(props) {
    const { type, idpHint, idpHintProviderDisplayName, firstFeatureGFIDProvider, firstFeaturedGap, googleButton, appleButton, microsoftButton, clearButton, lastButton, } = props;
    const { hasFirstFeatured, featuredProvider, isHint, buttons } = getAuthButtonOrder(type, firstFeatureGFIDProvider, idpHint);
    const isIdpProviderFeatured = (featuredProvider === GFIDProvider.GOOGLE && !!googleButton) ||
        (featuredProvider === GFIDProvider.APPLE && !!appleButton) ||
        (featuredProvider === GFIDProvider.MICROSOFT && !!microsoftButton);
    const idpHintMessage = hasFirstFeatured && isIdpProviderFeatured && idpHintProviderDisplayName ? (_jsx(IdpHintMessage, { children: t("You're already signed in to {{provider}}. Tap to continue, or choose another option below.", {
            provider: idpHintProviderDisplayName,
        }) })) : null;
    const buttonElements = filterNull(buttons.map((button, index) => {
        var _a;
        const isFirstFeatured = !!hasFirstFeatured && index === 0;
        let buttonElement = null;
        if (button === GFIDProvider.GOOGLE && googleButton) {
            buttonElement = googleButton === null || googleButton === void 0 ? void 0 : googleButton(isFirstFeatured, isHint);
        }
        else if (button === GFIDProvider.APPLE && appleButton) {
            buttonElement = appleButton === null || appleButton === void 0 ? void 0 : appleButton(isFirstFeatured, isHint);
        }
        else if (button === GFIDProvider.MICROSOFT && microsoftButton) {
            buttonElement = microsoftButton === null || microsoftButton === void 0 ? void 0 : microsoftButton(isFirstFeatured, isHint);
        }
        else if (button === GFIDProvider.CLEAR && clearButton) {
            buttonElement = clearButton === null || clearButton === void 0 ? void 0 : clearButton(isFirstFeatured, isHint);
        }
        else if (button === "LAST" && lastButton) {
            buttonElement = lastButton === null || lastButton === void 0 ? void 0 : lastButton(isFirstFeatured, isHint);
        }
        if (buttonElement && isFirstFeatured && firstFeaturedGap) {
            return (_jsxs(React.Fragment, { children: [buttonElement, firstFeaturedGap()] }, `${(_a = buttonElement.key) !== null && _a !== void 0 ? _a : ""}-with-first-featured-gap`));
        }
        else {
            return buttonElement;
        }
    }));
    return idpHintMessage ? [idpHintMessage, ...buttonElements] : buttonElements;
}
export const FirstFeaturedGapComponent = () => {
    const { t } = useTranslation(["common"]);
    return (_jsxs(FirstFeaturedGap, { children: [_jsx(FirstFeaturedGapDivider, { children: _jsx(FirstFeaturedGapDividerLine, {}) }), _jsx(FirstFeaturedGapText, { children: t("or") }), _jsx(FirstFeaturedGapDivider, { children: _jsx(FirstFeaturedGapDividerLine, {}) })] }));
};
